import { assert } from '@varos/util-typescript';
import { IntegrationLoaderBaseConfig } from '../../../base';
import { ThreadDetailLoader } from './integrationDetailInterface';
import { IntegrationDetailAggregate } from './integrationDetailModel';
import { AssistantContext } from '@varos/assistant-react';
import { useMemo } from 'react';
import { MessageItemAggregate } from '../../../../chat/message/item';

export function createThreadDetailLoader(
    config: IntegrationLoaderBaseConfig
): ThreadDetailLoader {
    const { repository } = config;
    return {
        useLoad(context, props): IntegrationDetailAggregate {
            // First, load the thread using the thread ID
            const threadQuery = repository.assistant.local.thread.useLookup(
                context,
                { id: props.item.id },
                {
                    suspense: true,
                }
            );
            assert(threadQuery.status === 'success', 'expected suspense for thread');

            // Get integration ID from thread metadata
            const integrationId = threadQuery.data.metadata.integrationId as string;
            assert(integrationId, 'thread must have integrationId in metadata');

            // Load the integration using the integration ID from thread metadata
            const integrationQuery = repository.platform.integration.useRetrieve(
                context,
                integrationId,
                {
                    suspense: true,
                }
            );
            assert(
                integrationQuery.status === 'success',
                'expected suspense for integration'
            );

            // Load the integration definition
            const integrationDefinitionQuery =
                repository.platform.integrationDefinition.useRetrieve(
                    context,
                    integrationQuery.data.definition_id,
                    {
                        suspense: true,
                    }
                );
            assert(
                integrationDefinitionQuery.status === 'success',
                'expected suspense for integration definition'
            );

            // Load messages for this thread
            const messagesQuery = repository.assistant.local.message.useFind(
                context as AssistantContext,
                { thread: props.item.id, limit: 25 },
                {
                    suspense: true,
                    staleTime: 0,
                    refetchOnMount: true,
                    refetchOnWindowFocus: true,
                    refetchOnReconnect: true,
                }
            );
            assert(messagesQuery.status === 'success', 'expected suspense for messages');

            const messages = useMemo(
                () =>
                    messagesQuery.data
                        .filter(
                            // NOTE do not display system messages
                            (item) => item.role === 'user' || item.role === 'assistant'
                        )
                        .map(
                            (msg): MessageItemAggregate => ({
                                definition: integrationDefinitionQuery.data,
                                instance: integrationQuery.data,
                                message: msg,
                            })
                        ),
                [
                    messagesQuery.data,
                    integrationQuery.data,
                    integrationDefinitionQuery.data,
                ]
            );

            // console.log('messages', messages);

            return {
                thread: {
                    thread: threadQuery.data,
                    messages,
                    integration: integrationQuery.data,
                    definition: integrationDefinitionQuery.data,
                },
                integration: integrationQuery.data,
                definition: integrationDefinitionQuery.data,
            };
        },
    };
}
